/**
 * Application providers.
 *
 * Deliberately short. There is no HTTP client, no interceptor chain and no
 * store library: the only data source is `GraphqlClient`, which resolves
 * `MockBackend` from the injector, and application state lives in two signal
 * services (tech spec §4.2). Adding a provider here should require a reason.
 */
import {
  type ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { provideAdapterModeSwitch } from './dev/adapter-mode.switch';
import { provideBlockOutcomeSwitch } from './dev/block-outcome.switch';
import { provideFeatureFlagSwitch } from './dev/feature-flag.switch';
import { provideInstalmentOutcomeSwitch } from './dev/instalment-outcome.switch';
import { provideVerificationEstimateSwitch } from './dev/verification-estimate.switch';

export const appConfig: ApplicationConfig = {
  providers: [
    // Zoneless: there is no zone.js in the polyfill list, and every screen is
    // OnPush over signals, so there is nothing for zone patching to do.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      // Route params arrive as signal inputs — `/migrare/telefon/:id` and
      // `/migrare/comanda/:ref` read them with `input.required<string>()`.
      withComponentInputBinding(),
      // The flow is a sequence of full-page steps. Landing halfway down a step
      // because the previous one was scrolled is disorienting; anchors still
      // work for the in-page error summaries.
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
    ),
    // POC scaffolding — the five providers here that are not product wiring. Each
    // reaches a state that has no other entrance until the dev overlay exists, and
    // each does nothing at all unless its parameter is used.
    //
    // `?rate=block|upfront|sim` → S-12's three declined-instalments variants.
    provideInstalmentOutcomeSwitch(),
    // `?sla=on` → S-14 with a verification timeframe instead of without one (Q-19).
    provideVerificationEstimateSwitch(),
    // `?block=cap:degraded` or `?block=all:redirected` → any of the fourteen blocked
    // triggers shown as BLOCKED, DEGRADED or REDIRECTED. Eleven have no assigned
    // outcome, so the POC's default is a guess and has to be flippable.
    provideBlockOutcomeSwitch(),
    // `?port=companyRegistry:fail` → one simulated system in a failure mode. The only
    // way to reach four of the triggers, which are systems failing rather than
    // properties of a customer.
    provideAdapterModeSwitch(),
    // `?flag=off` → FEATURE_DISABLED, the first of the nine eligibility checks (Q-27).
    provideFeatureFlagSwitch(),
  ],
};
